/**
 * RequestMeetingPage
 *
 * Form to request a meeting with the account representative.
 * Includes date picker, topic, and notes fields.
 * Shows pending meeting requests below the form.
 *
 * Requirements: Task 14 - Meeting Request Page
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Textarea,
  useToast,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@bloomcode/ui';
import { CalendarDays, Send } from '@bloomcode/icons';

import { usePendingMeetings, useRequestMeeting } from '@/hooks/useMeetingRequests';
import { PendingMeetings } from '@/components/meetings';

// Validation schema
const requestMeetingSchema = z.object({
  preferredDate: z.string().min(1, 'Preferred date is required'),
  topic: z.string().min(1, 'Topic is required').max(200, 'Topic must be under 200 characters'),
  notes: z.string().max(1000, 'Notes must be under 1000 characters').optional().or(z.literal('')),
});

type RequestMeetingFormValues = z.infer<typeof requestMeetingSchema>;

export default function RequestMeetingPage() {
  const { toast } = useToast();
  const { requestMeeting, isRequesting } = useRequestMeeting();
  const { meetings, isLoading: meetingsLoading, refetch } = usePendingMeetings();

  const form = useForm<RequestMeetingFormValues>({
    resolver: zodResolver(requestMeetingSchema),
    defaultValues: {
      preferredDate: '',
      topic: '',
      notes: '',
    },
  });

  const onSubmit = async (values: RequestMeetingFormValues) => {
    const result = await requestMeeting({
      preferredDate: new Date(values.preferredDate).toISOString(),
      topic: values.topic,
      notes: values.notes || undefined,
    });

    if (result.success) {
      toast({
        title: 'Meeting request submitted',
        description: 'Your account representative will be notified.',
      });
      form.reset();
      refetch();
    } else {
      toast({ title: result.message ?? 'Failed to submit request', variant: 'destructive' });
    }
  };

  // Get minimum date (today)
  const minDate = new Date().toISOString().slice(0, 16);

  return (
    <>
      <Helmet>
        <title>Request Meeting | Bloomcode CRM Portal</title>
      </Helmet>

      <div className="container mx-auto p-6">
        {/* Breadcrumb */}
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/dashboard">Dashboard</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Request Meeting</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="mb-6">
          <h1 className="text-2xl font-heading font-bold">Request Meeting</h1>
          <p className="text-muted-foreground">
            Schedule a meeting with your account representative.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Meeting Request Form */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-primary" />
                New Meeting Request
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                {/* Preferred Date */}
                <div className="space-y-1.5">
                  <label htmlFor="preferredDate" className="text-sm font-medium text-foreground">
                    Preferred Date & Time <span className="text-destructive">*</span>
                  </label>
                  <Input
                    id="preferredDate"
                    type="datetime-local"
                    min={minDate}
                    aria-invalid={!!form.formState.errors.preferredDate}
                    aria-describedby={
                      form.formState.errors.preferredDate ? 'preferredDate-error' : undefined
                    }
                    {...form.register('preferredDate')}
                  />
                  {form.formState.errors.preferredDate && (
                    <p id="preferredDate-error" className="text-sm text-destructive" role="alert">
                      {form.formState.errors.preferredDate.message}
                    </p>
                  )}
                </div>

                {/* Topic */}
                <div className="space-y-1.5">
                  <label htmlFor="topic" className="text-sm font-medium text-foreground">
                    Topic <span className="text-destructive">*</span>
                  </label>
                  <Input
                    id="topic"
                    type="text"
                    placeholder="What would you like to discuss?"
                    aria-invalid={!!form.formState.errors.topic}
                    aria-describedby={form.formState.errors.topic ? 'topic-error' : undefined}
                    {...form.register('topic')}
                  />
                  {form.formState.errors.topic && (
                    <p id="topic-error" className="text-sm text-destructive" role="alert">
                      {form.formState.errors.topic.message}
                    </p>
                  )}
                </div>

                {/* Notes */}
                <div className="space-y-1.5">
                  <label htmlFor="notes" className="text-sm font-medium text-foreground">
                    Additional Notes
                  </label>
                  <Textarea
                    id="notes"
                    placeholder="Any additional context or preparation needed..."
                    rows={4}
                    aria-invalid={!!form.formState.errors.notes}
                    aria-describedby={form.formState.errors.notes ? 'notes-error' : undefined}
                    {...form.register('notes')}
                  />
                  {form.formState.errors.notes && (
                    <p id="notes-error" className="text-sm text-destructive" role="alert">
                      {form.formState.errors.notes.message}
                    </p>
                  )}
                </div>

                {/* Submit */}
                <div className="flex justify-end pt-2">
                  <Button type="submit" disabled={isRequesting}>
                    <Send className="mr-2 h-4 w-4" />
                    {isRequesting ? 'Submitting...' : 'Request Meeting'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Pending Meetings */}
          <PendingMeetings meetings={meetings} isLoading={meetingsLoading} />
        </div>
      </div>
    </>
  );
}
